import type { MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import useListTruncator from 'hooks/useListTruncator'
import CruiseOfferInclusionItem from './CruiseOfferInclusionItem'

const DEFAULT_VISIBLE_ITEMS = 3

const InclusionItemWrapper = styled.div`
  min-width: 0;
`

const ToggleText = styled(TextLink)`
  white-space: nowrap;
`

interface Props {
  inclusions: Array<App.PackageInclusion>
  visibleItems?: number
}

function CruiseLimitedInclusionList({ inclusions, visibleItems = DEFAULT_VISIBLE_ITEMS }: Props) {
  const intl = useIntl()
  const [showAll, setShowAll] = useState(false)
  const [visibleInclusions, hiddenInclusions] = useListTruncator(showAll ? undefined : visibleItems, inclusions)
  const hiddenCount = hiddenInclusions.length

  const onShowMoreClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setShowAll(true)
  }, [])

  const onShowLessClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setShowAll(false)
  }, [])

  return <Group
    wrap="wrap"
    direction="vertical"
    gap={4}
    verticalAlign="center"
    horizontalAlign="start"
  >
    {visibleInclusions.map((inclusion, index) => {
      const shouldShowMoreToggle = !showAll && hiddenCount > 0 && index === visibleInclusions.length - 1

      return <InclusionItemWrapper key={inclusion.id}>
        <CruiseOfferInclusionItem
          inclusion={inclusion}
          showMinStayDescription
          showMoreLink={shouldShowMoreToggle && <ToggleText
            size="medium"
            colour="normal-contrast"
            onClick={onShowMoreClick}
          >
            {intl.formatMessage({
              id: 'UtomIJF0H414JjvYQR_T',
              defaultMessage: '+{count} more',
              description: 'Action to expand and show more evergreen cruise inclusions',
            }, { count: hiddenCount })}
          </ToggleText>}
        />
      </InclusionItemWrapper>
    })}

    {showAll && inclusions.length > visibleItems && <ToggleText size="medium" colour="normal-contrast" onClick={onShowLessClick}>
      {intl.formatMessage({
        id: 'H9GcFGylDblx4phqaAZa',
        defaultMessage: 'Show less',
        description: 'Action to collapse expanded evergreen cruise inclusions',
      })}
    </ToggleText>}
  </Group>
}

export default CruiseLimitedInclusionList
